import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { extractActivitiesFromResponse, fetchActivities } from '../api/activities';
import {
  extractBannersFromResponse,
  fetchBanners,
  getBannerImageUrl,
} from '../api/banners';
import {
  extractContentsFromResponse,
  fetchContents,
  getContentActivityLabel,
  getContentImageUrl,
} from '../api/contents';
import { getApiErrorMessage } from '../api/errors';
import { showError } from './adminAlerts';
import AdminLoader from './AdminLoader';
import './AdminDashboard.css';

function AdminDashboard() {
  const [banners, setBanners] = useState([]);
  const [activities, setActivities] = useState([]);
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);

    try {
      const [bannerResponse, activityResponse, contentResponse] = await Promise.all([
        fetchBanners(),
        fetchActivities(),
        fetchContents(),
      ]);

      setBanners(extractBannersFromResponse(bannerResponse));
      setActivities(extractActivitiesFromResponse(activityResponse));
      setContents(extractContentsFromResponse(contentResponse));
      setLastUpdated(new Date());
    } catch (err) {
      showError(getApiErrorMessage(err, 'Could not load dashboard data.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const activitiesWithPhotos = useMemo(() => {
    const ids = new Set(contents.map((c) => c.activity_id));
    return ids.size;
  }, [contents]);

  const photoCoverage = activities.length
    ? Math.round((activitiesWithPhotos / activities.length) * 100)
    : 0;

  const recentItems = useMemo(() => {
    const items = [
      ...banners.map((b) => ({
        id: b.id,
        type: 'Banner',
        name: b.title,
        image: getBannerImageUrl(b),
        link: '/admin/banner',
      })),
      ...activities.map((a) => ({
        id: a.id,
        type: 'Activity',
        name: a.name,
        image: null,
        initial: a.name?.charAt(0)?.toUpperCase() || '?',
        link: '/admin/activity',
      })),
      ...contents.map((c) => ({
        id: c.id,
        type: 'Content',
        name: getContentActivityLabel(c),
        image: getContentImageUrl(c),
        link: '/admin/content',
      })),
    ];
    return items.slice(0, 8);
  }, [banners, activities, contents]);

  const syncLabel = lastUpdated
    ? `Last synced ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : 'Not synced yet';

  const kpis = [
    {
      label: 'Banners',
      value: banners.length,
      desc: 'Carousel slides on homepage',
      to: '/admin/banner',
    },
    {
      label: 'Activities',
      value: activities.length,
      desc: 'Event categories in ticker',
      to: '/admin/activity',
    },
    {
      label: 'Photos',
      value: contents.length,
      desc: 'Images linked to activities',
      to: '/admin/content',
    },
  ];

  return (
    <div className="dash">
      <header className="dash-header">
        <div>
          <h1 className="dash-header__title">Dashboard</h1>
          <p className="dash-header__meta">
            {syncLabel}
            {isLoading && ' · Updating…'}
          </p>
        </div>
        <button
          type="button"
          className="dash-header__refresh"
          onClick={loadDashboard}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <AdminLoader label="" size="sm" inline />
              Updating…
            </>
          ) : (
            'Refresh data'
          )}
        </button>
      </header>

      <div className="dash-kpis">
        {kpis.map((kpi) => (
          <article key={kpi.label} className="dash-kpi">
            <div className="dash-kpi__top">
              <span className="dash-kpi__label">{kpi.label}</span>
              <Link to={kpi.to} className="dash-kpi__link">
                Manage
              </Link>
            </div>
            <p className="dash-kpi__value">{isLoading ? '—' : kpi.value}</p>
            <p className="dash-kpi__desc">{kpi.desc}</p>
          </article>
        ))}
      </div>

      <div className="dash-grid">
        <section className="dash-panel dash-panel--wide">
          <div className="dash-panel__head">
            <h2 className="dash-panel__title">Recent updates</h2>
            <span className="dash-panel__badge">
              {isLoading ? '—' : recentItems.length} shown
            </span>
          </div>

          {isLoading ? (
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>ID</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4].map((row) => (
                    <tr key={row} className="dash-table__row--loading">
                      <td><span className="dash-skeleton" /></td>
                      <td><span className="dash-skeleton dash-skeleton--long" /></td>
                      <td><span className="dash-skeleton dash-skeleton--short" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : recentItems.length === 0 ? (
            <div className="dash-empty">
              <p>No content published yet.</p>
              <div className="dash-empty__actions">
                <Link to="/admin/banner" className="dash-empty__btn">Add banner</Link>
                <Link to="/admin/activity" className="dash-empty__btn">Add activity</Link>
                <Link to="/admin/content" className="dash-empty__btn">Upload photo</Link>
              </div>
            </div>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>ID</th>
                  </tr>
                </thead>
                <tbody>
                  {recentItems.map((item) => (
                    <tr key={`${item.type}-${item.id}`}>
                      <td>
                        <span className="dash-tag">{item.type}</span>
                      </td>
                      <td>
                        <Link to={item.link} className="dash-table__name">
                          {item.image ? (
                            <img src={item.image} alt="" className="dash-table__thumb" />
                          ) : (
                            <span className="dash-table__initial">{item.initial}</span>
                          )}
                          <span>{item.name}</span>
                        </Link>
                      </td>
                      <td className="dash-table__id">{item.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="dash-side">
          <section className="dash-panel">
            <h2 className="dash-panel__title">Content health</h2>
            <div className="dash-health">
              <div className="dash-health__row">
                <span>Activities with photos</span>
                <strong>{isLoading ? '—' : `${activitiesWithPhotos} / ${activities.length}`}</strong>
              </div>
              <div className="dash-health__track">
                <div
                  className="dash-health__bar"
                  style={{ width: isLoading ? '0%' : `${photoCoverage}%` }}
                />
              </div>
              <p className="dash-health__note">
                {isLoading
                  ? 'Calculating coverage…'
                  : `${photoCoverage}% of activities have at least one photo.`}
              </p>
            </div>
          </section>

          <section className="dash-panel">
            <h2 className="dash-panel__title">Quick actions</h2>
            <nav className="dash-actions">
              <Link to="/admin/banner" className="dash-actions__item">
                <span>New banner</span>
                <span aria-hidden="true">→</span>
              </Link>
              <Link to="/admin/activity" className="dash-actions__item">
                <span>New activity</span>
                <span aria-hidden="true">→</span>
              </Link>
              <Link to="/admin/content" className="dash-actions__item">
                <span>Upload photo</span>
                <span aria-hidden="true">→</span>
              </Link>
              <Link to="/" className="dash-actions__item dash-actions__item--muted">
                <span>View public site</span>
                <span aria-hidden="true">→</span>
              </Link>
            </nav>
          </section>
        </aside>
      </div>
    </div>
  );
}

export default AdminDashboard;
